import React, { useState, useEffect } from 'react';
import {
  DollarSign, Plus, X, Store, Calendar, Filter,
  RefreshCw, Tag, Edit2, Trash2, Save, CheckCircle,
  AlertTriangle, TrendingDown, Settings
} from 'lucide-react';
import api, { API_URL } from '../../services/api';

const Depenses = () => {
  const [depenses, setDepenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [boutiques, setBoutiques] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filtres
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
  const [filtreBoutique, setFiltreBoutique] = useState('');
  const [filtreCategorie, setFiltreCategorie] = useState('');
  const [dateDebut, setDateDebut] = useState(today.slice(0, 8) + '01');
  const [dateFin, setDateFin] = useState(today);

  // Modals
  const [showForm, setShowForm] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    date_depense: today, categorie_id: '', boutique_id: '',
    description: '', montant: '', mode_paiement: 'especes', reference: '', notes: ''
  });
  const [newCat, setNewCat] = useState({ nom: '', couleur: '#6B7280' });
  const [submitting, setSubmitting] = useState(false);

  const fmt = (m) => parseFloat(m||0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' DH';

  // API
  const loadAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtreBoutique) params.append('boutique_id', filtreBoutique);
      if (filtreCategorie) params.append('categorie_id', filtreCategorie);
      if (dateDebut) params.append('date_debut', dateDebut);
      if (dateFin) params.append('date_fin', dateFin);
      const [depR, catR, boutR, statsR] = await Promise.all([
        api.get(`${API_URL}/depenses?${params.toString()}`),
        api.get(`${API_URL}/depenses/categories`),
        api.get(`${API_URL}/boutiques`),
        api.get(`${API_URL}/depenses/stats?${params.toString()}`),
      ]);
      setDepenses(depR.data || []);
      setCategories(catR.data || []);
      setBoutiques(Array.isArray(boutR.data) ? boutR.data : boutR.data?.boutiques || []);
      setStats(statsR.data);
    } catch (err) { setError('Erreur chargement'); console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const handleSubmit = async () => {
    if (!form.montant || parseFloat(form.montant) <= 0) { setError('Montant requis'); return; }
    setSubmitting(true); setError('');
    try {
      if (editId) {
        await api.put(`${API_URL}/depenses/${editId}`, form);
        setSuccess('Depense modifiee');
      } else {
        await api.post(`${API_URL}/depenses`, form);
        setSuccess('Depense enregistree');
      }
      setShowForm(false); setEditId(null);
      setForm({ date_depense: today, categorie_id: '', boutique_id: '', description: '', montant: '', mode_paiement: 'especes', reference: '', notes: '' });
      await loadAll();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette depense ?')) return;
    try {
      await api.delete(`${API_URL}/depenses/${id}`);
      setSuccess('Depense supprimee');
      await loadAll();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError('Erreur suppression'); }
  };

  const handleEdit = (d) => {
    setForm({
      date_depense: d.date_depense ? new Date(d.date_depense).toISOString().split('T')[0] : today,
      categorie_id: d.categorie_id || '', boutique_id: d.boutique_id || '',
      description: d.description || '', montant: d.montant || '',
      mode_paiement: d.mode_paiement || 'especes', reference: d.reference || '', notes: d.notes || ''
    });
    setEditId(d.id);
    setShowForm(true);
  };

  const handleAddCategory = async () => {
    if (!newCat.nom.trim()) return;
    try {
      await api.post(`${API_URL}/depenses/categories`, newCat);
      setNewCat({ nom: '', couleur: '#6B7280' });
      await loadAll();
    } catch (err) { setError('Erreur ajout categorie'); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div></div>;

  const totalPeriode = depenses.reduce((s, d) => s + parseFloat(d.montant || 0), 0);

  return (
    <div className="min-h-screen bg-gray-100 pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-rose-700 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold">Depenses & Charges</h1>
              <p className="text-red-200 text-sm">Suivi des depenses par boutique et categorie</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setEditId(null); setForm({ date_depense: today, categorie_id: '', boutique_id: '', description: '', montant: '', mode_paiement: 'especes', reference: '', notes: '' }); setShowForm(true); }}
                className="flex items-center space-x-2 bg-white text-red-700 px-4 py-2 rounded-lg font-medium hover:bg-red-50">
                <Plus className="h-4 w-4" /><span>Nouvelle depense</span>
              </button>
              <button onClick={() => setShowCategories(true)} className="bg-white/20 hover:bg-white/30 p-2.5 rounded-lg" title="Gerer les categories">
                <Settings className="h-4 w-4" />
              </button>
              <button onClick={loadAll} className="bg-white/20 hover:bg-white/30 p-2.5 rounded-lg">
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-6">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between"><span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{error}</span><button onClick={() => setError('')}><X className="h-4 w-4" /></button></div>}
        {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2"><CheckCircle className="h-4 w-4" />{success}</div>}

        {/* Filtres */}
        <div className="bg-white rounded-xl shadow-sm border p-4 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Boutique</label>
            <select value={filtreBoutique} onChange={e => setFiltreBoutique(e.target.value)}
              className="px-3 py-2 border rounded-lg bg-white text-sm">
              <option value="">Toutes</option>
              {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Categorie</label>
            <select value={filtreCategorie} onChange={e => setFiltreCategorie(e.target.value)}
              className="px-3 py-2 border rounded-lg bg-white text-sm">
              <option value="">Toutes</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Du</label>
            <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Au</label>
            <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <button onClick={loadAll} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
            <Filter className="h-4 w-4" />
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-xs text-gray-500">Total periode</p>
            <p className="text-2xl font-black text-red-600">{fmt(totalPeriode)}</p>
            <p className="text-xs text-gray-400">{depenses.length} depense{depenses.length > 1 ? 's' : ''}</p>
          </div>
          {(stats?.par_categorie || []).filter(c => parseFloat(c.total) > 0).slice(0, 3).map(c => (
            <div key={c.id} className="bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.couleur }} />
                <p className="text-xs text-gray-500">{c.nom}</p>
              </div>
              <p className="text-xl font-black text-gray-900">{fmt(c.total)}</p>
            </div>
          ))}
        </div>

        {/* Par boutique */}
        {(stats?.par_boutique || []).length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.par_boutique.filter(b => parseFloat(b.total) > 0).map(b => (
              <div key={b.boutique_id} className="bg-white rounded-xl shadow-sm border p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Store className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="font-bold text-gray-900">{b.boutique_nom || 'Non affecte'}</p>
                    <p className="text-xs text-gray-500">{b.nb} depense{b.nb > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <p className="text-lg font-black text-red-600">{fmt(b.total)}</p>
              </div>
            ))}
          </div>
        )}

        {/* Liste depenses */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <h2 className="font-bold text-gray-800 flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-red-600" />
              <span>DEPENSES ({depenses.length})</span>
            </h2>
            <p className="text-lg font-black text-red-600">{fmt(totalPeriode)}</p>
          </div>

          {depenses.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucune depense sur cette periode</p>
            </div>
          ) : (
            <>
              {/* Mobile */}
              <div className="md:hidden divide-y">
                {depenses.map(d => (
                  <div key={d.id} className="p-4">
                    <div className="flex items-start justify-between mb-1">
                      <div>
                        <p className="font-medium text-gray-900">{d.description || 'Sans description'}</p>
                        <div className="flex gap-1.5 mt-1 flex-wrap">
                          {d.categorie_nom && <span className="text-xs px-2 py-0.5 rounded-full text-white font-medium" style={{ backgroundColor: d.categorie_couleur }}>{d.categorie_nom}</span>}
                          {d.boutique_nom && <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">{d.boutique_nom}</span>}
                        </div>
                      </div>
                      <p className="text-lg font-black text-red-600">{fmt(d.montant)}</p>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <p className="text-xs text-gray-500">{d.date_depense ? new Date(d.date_depense).toLocaleDateString('fr-FR') : '-'} - {d.mode_paiement}</p>
                      <div className="flex gap-2">
                        <button onClick={() => handleEdit(d)} className="text-blue-600"><Edit2 className="h-4 w-4" /></button>
                        <button onClick={() => handleDelete(d.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Categorie</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Boutique</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Mode</th>
                      <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Montant</th>
                      <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {depenses.map(d => (
                      <tr key={d.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-600">{d.date_depense ? new Date(d.date_depense).toLocaleDateString('fr-FR') : '-'}</td>
                        <td className="px-4 py-3">
                          {d.categorie_nom ? (
                            <span className="text-xs px-2 py-1 rounded-full text-white font-medium" style={{ backgroundColor: d.categorie_couleur }}>{d.categorie_nom}</span>
                          ) : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-700">{d.boutique_nom || '-'}</td>
                        <td className="px-4 py-3 text-gray-800">{d.description || '-'}</td>
                        <td className="px-4 py-3 text-center"><span className="text-xs px-2 py-0.5 rounded bg-gray-100">{d.mode_paiement}</span></td>
                        <td className="px-4 py-3 text-right font-black text-red-600">{fmt(d.montant)}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center gap-2">
                            <button onClick={() => handleEdit(d)} className="text-blue-600 hover:text-blue-800"><Edit2 className="h-4 w-4" /></button>
                            <button onClick={() => handleDelete(d.id)} className="text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ===== MODAL NOUVELLE DEPENSE ===== */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editId ? 'Modifier la depense' : 'Nouvelle depense'}</h3>
              <button onClick={() => { setShowForm(false); setEditId(null); }} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Date *</label>
                  <input type="date" value={form.date_depense} onChange={e => setForm({...form, date_depense: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Montant (DH) *</label>
                  <input type="text" inputMode="decimal" value={form.montant} onChange={e => setForm({...form, montant: e.target.value})}
                    onFocus={e => e.target.select()}
                    className="w-full px-3 py-2 border rounded-lg text-lg font-bold text-red-600" placeholder="0.00" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Categorie</label>
                <select value={form.categorie_id} onChange={e => setForm({...form, categorie_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg">
                  <option value="">-- Choisir --</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Boutique</label>
                <select value={form.boutique_id} onChange={e => setForm({...form, boutique_id: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg">
                  <option value="">-- Non affecte --</option>
                  {boutiques.map(b => <option key={b.id} value={b.id}>{b.nom}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Description</label>
                <input type="text" value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg" placeholder="Ex: Carrelage boutique Marrakech" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Mode paiement</label>
                  <select value={form.mode_paiement} onChange={e => setForm({...form, mode_paiement: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg">
                    <option value="especes">Especes</option>
                    <option value="cheque">Cheque</option>
                    <option value="virement">Virement</option>
                    <option value="carte">Carte</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Reference</label>
                  <input type="text" value={form.reference} onChange={e => setForm({...form, reference: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg" placeholder="N. cheque, facture..." />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg" rows="2" placeholder="Notes..." />
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-3">
              <button onClick={() => { setShowForm(false); setEditId(null); }} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">Annuler</button>
              <button onClick={handleSubmit} disabled={submitting}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2">
                <Save className="h-4 w-4" /><span>{submitting ? 'En cours...' : editId ? 'Modifier' : 'Enregistrer'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL CATEGORIES ===== */}
      {showCategories && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2"><Tag className="h-5 w-5" />Categories de depenses</h3>
              <button onClick={() => setShowCategories(false)} className="hover:bg-white/20 p-1 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              {/* Ajouter */}
              <div className="flex gap-2">
                <input type="text" value={newCat.nom} onChange={e => setNewCat({...newCat, nom: e.target.value})}
                  className="flex-1 px-3 py-2 border rounded-lg text-sm" placeholder="Nom de la categorie" />
                <input type="color" value={newCat.couleur} onChange={e => setNewCat({...newCat, couleur: e.target.value})}
                  className="w-10 h-10 rounded-lg border cursor-pointer" />
                <button onClick={handleAddCategory}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              {/* Liste */}
              <div className="divide-y">
                {categories.map(c => (
                  <div key={c.id} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: c.couleur }} />
                      <span className="font-medium text-gray-800">{c.nom}</span>
                    </div>
                    {c.description && <span className="text-xs text-gray-400">{c.description}</span>}
                  </div>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-end">
              <button onClick={() => setShowCategories(false)} className="px-6 py-2 bg-gray-800 text-white rounded-lg font-medium">Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Depenses;
